import { useMediaQueryDevice } from '@/components/MediaQuery';
import { AIP_DOMAIN, SYN_API_URLS } from '@/constants/api';
import { useMainPosition } from '@/features/account/positionHook';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useTradeStore } from '@/features/trade/store';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { sleep, tweet } from '@/utils/common';
import { exportAsImage, exportAsImageDataURL, uploadImageAndGetId } from '@/utils/image';
import { getSharePNLId } from '@/utils/sharepnl';
import { showProperDateString } from '@/utils/timeUtils';
import {
  OptionId,
  PnlShareSettingProps,
  TSynSharePnlIdTimestampMaps,
  filterSynSharePnlIdTimestampMaps,
  getInitPnlShareSetting,
  pnlShareSettingStorageKey,
} from '@/utils/trade/pnlShare';
import _ from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const optionInfos: { i18nId: string; optionId: OptionId }[] = [
  {
    i18nId: 'common.leverage',
    optionId: OptionId.leverage,
  },
  {
    i18nId: 'common.pnlShareModal.pnlAmount',
    optionId: OptionId.pnlAmount,
  },
  {
    i18nId: 'common.price',
    optionId: OptionId.price,
  },
];
type TBtnStatus = 'init' | 'loading' | 'done' | 'error';
export const pnlStorageKey = 'SYN_SHARE_PNL_ID_TIMESTAMP_MAP_0.0.3';
export const pnlNotiStorageKey = 'SYN_SHARE_PNL_Noti_ID_TIMESTAMP_MAP_0.0.1';

const clearUpOldKey = () => {
  localStorage.removeItem('SYN_SHARE_PNL_ID_TIMESTAMP_MAP_0.0.1');
  localStorage.removeItem('SYN_SHARE_PNL_ID_TIMESTAMP_MAP_0.0.2');
};
type TBtnId = 'copy' | 'download' | 'tweet';
clearUpOldKey();
const getSynSharePnlIdTimestampMaps = (storageKey: string) =>
  JSON.parse(localStorage.getItem(storageKey) || '{}') as TSynSharePnlIdTimestampMaps;
const setSynSharePnlIdTimestampMaps = (synSharePnlIdTimestampMaps: TSynSharePnlIdTimestampMaps, storageKey: string) =>
  localStorage.setItem(storageKey, JSON.stringify(synSharePnlIdTimestampMaps));

export default function usePnlSharePanel({
  cardRef,
  storageKey,
}: {
  cardRef: RefObject<HTMLDivElement>;
  storageKey: string;
}) {
  const initPnlShareSetting = getInitPnlShareSetting();
  const [btnStatus, setBtnStatus] = useState<{
    [id in TBtnId]: TBtnStatus;
  }>({ copy: 'init', download: 'init', tweet: 'init' });
  const [btnMessage, setBtnMessage] = useState<{
    [id in TBtnId]: string | null;
  }>({ copy: null, download: null, tweet: null });
  const [optionStatus, setOptionStatus] = useState<PnlShareSettingProps>(initPnlShareSetting);
  const { t } = useTranslation();
  const handleError = useCallback(
    async (btnId: TBtnId) => {
      setBtnStatus((prev) => {
        return { ...prev, [btnId]: 'error' };
      });
      setBtnMessage((prev) => {
        return { ...prev, [btnId]: t('common.pnlShareModal.tooltip.error') };
      });
      await sleep(2500);
      setBtnStatus((prev) => {
        return { ...prev, [btnId]: 'init' };
      });
      setBtnMessage((prev) => {
        return { ...prev, [btnId]: null };
      });
    },
    [t],
  );
  const handleFinish = useCallback(
    async (btnId: TBtnId) => {
      setBtnStatus((prev) => {
        return { ...prev, [btnId]: 'done' };
      });
      setBtnMessage((prev) => {
        return { ...prev, [btnId]: t(`common.pnlShareModal.tooltip.${btnId}`) };
      });
      await sleep(2500);
      setBtnStatus((prev) => {
        return { ...prev, [btnId]: 'init' };
      });
      setBtnMessage((prev) => {
        return { ...prev, [btnId]: null };
      });
    },
    [t],
  );
  const handleClickDownload = async () => {
    try {
      setBtnStatus((prev) => {
        return { ...prev, download: 'loading' };
      });
      await exportAsImage(cardRef.current, `syn-share-position-${Date.now()}.jpg`, { scale: 3 });
      handleFinish('download');
    } catch (error) {
      handleError('download');
    }
  };
  useEffect(() => {
    // clearup localstorage
    const oldMap = getSynSharePnlIdTimestampMaps(storageKey);
    setSynSharePnlIdTimestampMaps(oldMap ? filterSynSharePnlIdTimestampMaps(oldMap) : {}, storageKey);
    console.log('🚀 ~ clear up PnlShareId');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chainId = useChainId();
  const currentPairMobile = useCurrentPairFromUrl(chainId);
  // const currentPairDefault = useCurrentPairFromUrl(chainId);
  const userAddr = useUserAddr();
  const { isNotMobile } = useMediaQueryDevice();
  // const currentPair = isNotMobile ? currentPairDefault : currentPairMobile;
  // const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const mobilePosition = useMainPosition(chainId, userAddr, currentPairMobile?.id);
  const { pnlsharePosition } = useTradeStore();
  const currentPair = useMemo(
    () => (isNotMobile ? pnlsharePosition?.rootPair || currentPairMobile : currentPairMobile),
    [currentPairMobile, isNotMobile, pnlsharePosition?.rootPair],
  );
  const currentPosition = useMemo(
    () => (isNotMobile ? pnlsharePosition || mobilePosition : mobilePosition),
    [isNotMobile, mobilePosition, pnlsharePosition],
  );
  const rootInstrument = currentPosition?.rootInstrument;
  const getIsUploadRequired = useCallback(
    (shareId: string) => {
      const sharePnlIdTimestampMap =
        chainId &&
        userAddr &&
        currentPair?.id &&
        _.get(getSynSharePnlIdTimestampMaps(storageKey), [String(chainId), userAddr, currentPair.id]);
      if (sharePnlIdTimestampMap && Object.keys(sharePnlIdTimestampMap).includes(shareId)) {
        return false;
      }
      return true;
    },
    [chainId, currentPair?.id, storageKey, userAddr],
  );
  const handleUpload = useCallback(
    async ({
      onGetShareUrl,
      onFinish,
      onConfirmUpdateRequired,
      onError,
      onUpload,
    }: {
      onGetShareUrl?: (shareUrl: string) => void;
      onConfirmUpdateRequired?: () => void;
      onFinish?: (shareUrl: string, uploadUrl?: string) => Promise<void>;
      onError?: () => void;
      onUpload?: () => Promise<string>;
    }) => {
      try {
        const pairId = currentPair?.id;
        if (chainId && pairId && userAddr) {
          const timestamp = Date.now();
          const shareId = getSharePNLId({
            storageKey,
            chainId,
            pairId,
            userAddr,
            shareLeverage: optionStatus.leverage,
            sharePnl: optionStatus.pnlAmount,
            sharePrice: optionStatus.price,
            timestamp,
          });
          const shareUrl = `${AIP_DOMAIN}${SYN_API_URLS.OTHER.GET_TWITTER_IMG_LINK}?id=${shareId}&chainId=${chainId}&type=default`;
          onGetShareUrl?.(shareUrl);
          const isUpdateRequired = getIsUploadRequired(shareId);
          if (isUpdateRequired) {
            onConfirmUpdateRequired?.();
            const base64Data = await exportAsImageDataURL(cardRef.current, { scale: 2 });
            base64Data &&
              (await uploadImageAndGetId({
                id: shareId,
                base64Data,
                timestamp,
                chainId,
              }));
            setSynSharePnlIdTimestampMaps(
              _.set(
                getSynSharePnlIdTimestampMaps(storageKey) || {},
                [String(chainId), userAddr, currentPair.id, shareId],
                timestamp,
              ),
              storageKey,
            );
          }
          const uploadUrl = await onUpload?.();
          onFinish?.(shareUrl, uploadUrl);
        }
      } catch (error) {
        console.log('🚀 ~ usePnlSharePanel ~ error:', error);
        onError?.();
      }
    },
    [
      cardRef,
      chainId,
      currentPair?.id,
      getIsUploadRequired,
      optionStatus.leverage,
      optionStatus.pnlAmount,
      optionStatus.price,
      storageKey,
      userAddr,
    ],
  );
  const toggleOption = (optionId: OptionId) => {
    setOptionStatus((prev) => {
      const newOptionStatus = { ...prev, [optionId]: !prev[optionId] };
      localStorage.setItem(pnlShareSettingStorageKey, JSON.stringify(newOptionStatus));
      return newOptionStatus;
    });
  };

  return {
    handleClickDownload,
    handleCopy: useCallback(
      async () =>
        await handleUpload({
          onGetShareUrl: (shareUrl) => navigator.clipboard.writeText(shareUrl),
          onConfirmUpdateRequired: () =>
            setBtnStatus((prev) => {
              return { ...prev, copy: 'loading' };
            }),
          onFinish: async () => {
            handleFinish('copy');
          },
          onError: async () => {
            handleError('copy');
          },
        }),
      [handleError, handleFinish, handleUpload],
    ),
    handleTweet: useCallback(
      async () =>
        await handleUpload({
          onConfirmUpdateRequired: () =>
            setBtnStatus((prev) => {
              return { ...prev, tweet: 'loading' };
            }),
          onFinish: async (shareUrl) => {
            rootInstrument &&
              currentPair &&
              tweet({
                text: `Trading ${
                  rootInstrument.isInverse
                    ? `${rootInstrument.quoteToken.symbol}/${rootInstrument.baseToken?.symbol}`
                    : `${rootInstrument.baseToken.symbol}/${rootInstrument.quoteToken?.symbol}`
                } ${showProperDateString({
                  expiry: currentPair.expiry,
                  format: 'MMDD',
                })} on @SynFuturesDefi. 🚀 #CryptoTrading #DeFi`,
                url: shareUrl,
              });
            handleFinish('tweet');
          },
          onError: async () => {
            handleFinish('tweet');
          },
        }),
      [currentPair, handleFinish, handleUpload, rootInstrument],
    ),
    toggleOption,
    optionStatus,
    btnStatus,
    btnMessage,
  };
}
